import type {
  AiEvaluateRequest,
  AiEvaluateResponse,
  AiTutorGuidedAction,
  AiTutorRequest,
  AiTutorResponse,
  AiTutorTask,
} from './types';

type JsonRecord = Record<string, unknown>;

function asRecord(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' ? (value as JsonRecord) : null;
}

function asString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

function asTask(value: unknown): AiTutorTask | undefined {
  if (value === 'explain' || value === 'generate' || value === 'feedback') {
    return value;
  }
  return undefined;
}

function normalizeSuggestions(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => asString(item))
      .filter((item): item is string => Boolean(item));
    return normalized.length > 0 ? normalized : undefined;
  }

  const text = asString(value);
  if (!text) return undefined;

  const normalized = text
    .split(/\n|•/g)
    .map((item) => item.replace(/^\s*[-*]\s*/, '').trim())
    .filter(Boolean);
  return normalized.length > 0 ? normalized : [text];
}

function normalizeGuidedActions(value: unknown): AiTutorGuidedAction[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const normalized = value
    .map((item): AiTutorGuidedAction | null => {
      const rec = asRecord(item);
      if (!rec) return null;

      const id = asString(rec.id);
      const title = asString(rec.title);
      const label = asString(rec.label);

      if (!id && !title && !label) return null;

      const snakeNeedsUserText = rec['needs_user_text'];
      const needsUserText =
        typeof rec.needsUserText === 'boolean'
          ? rec.needsUserText
          : typeof snakeNeedsUserText === 'boolean'
            ? snakeNeedsUserText
            : undefined;

      return {
        id,
        title,
        label,
        description: asString(rec.description),
        task: asTask(rec.task),
        prompt: asString(rec.prompt),
        needsUserText,
        placeholder:
          asString(rec.placeholder) ??
          asString(rec.inputPlaceholder) ??
          asString(rec['input_placeholder']),
      };
    })
    .filter((item): item is AiTutorGuidedAction => item !== null);

  return normalized.length > 0 ? normalized : undefined;
}

export async function askTutor(req: AiTutorRequest): Promise<AiTutorResponse> {
  const res = await fetch('/api/ai/tutor', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'AI tutor request failed');
  }

  const payload = (await res.json()) as unknown;
  const rec = asRecord(payload);

  if (!rec) {
    throw new Error('Invalid AI tutor response format');
  }

  const explanation =
    asString(rec.explanation) ??
    asString(rec.answer) ??
    asString(rec.response) ??
    'پاسخی دریافت نشد. لطفاً دوباره تلاش کنید.';

  return {
    explanation,
    exercise: asString(rec.exercise) ?? asString(rec.practice),
    suggestions:
      normalizeSuggestions(rec.suggestions) ??
      normalizeSuggestions(rec.tips) ??
      normalizeSuggestions(rec.recommendations),
    encouragement: asString(rec.encouragement) ?? asString(rec.motivation),
    guidedActions:
      normalizeGuidedActions(rec.guidedActions) ??
      normalizeGuidedActions(rec['guided_actions']) ??
      normalizeGuidedActions(rec.actions),
    followUpPrompt:
      asString(rec.followUpPrompt) ??
      asString(rec['follow_up_prompt']) ??
      asString(rec.followupPrompt),
    feedbackPrompt:
      asString(rec.feedbackPrompt) ??
      asString(rec['feedback_prompt']) ??
      asString(rec.feedbackHint) ??
      asString(rec['feedback_hint']),
  };
}

export async function evaluateAnswer(req: AiEvaluateRequest): Promise<AiEvaluateResponse> {
  const res = await fetch('/api/ai/evaluate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || 'AI evaluate request failed');
  }

  return (await res.json()) as AiEvaluateResponse;
}
